import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect, useHistory, useLocation } from 'react-router-dom';
import PatientPanel from '@td/patient_panel';
import { Tabs, Tab, Loader } from '@td/ui';
import { translate } from '@td/shared_utils';
import ProviderPanel from '../provider-panel';
import { SpecialtyFilter } from './filters';
import SpecialtyProvider, { useSpecialty } from "./contexts/specialty-context";
import { LoaderContainer } from "./components";
import { specialtyCodes } from "./constants";

const PATIENT_PANEL_PATH = 'patients';
const PROVIDER_PANEL_PATH = 'providers';

export const Panels = ({ specialties, patientPanel, providerPanel }) => {
  const { specialty, enableStoredPreferences } = useSpecialty();
  const [currentTab, setCurrentTab] = useState(PATIENT_PANEL_PATH);

  const location = useLocation();
  const history = useHistory();

  const [patientPanelPath, providerPanelPath] = useMemo(() => {
    return [PATIENT_PANEL_PATH, PROVIDER_PANEL_PATH].map(id => window.Routes.panel_path({ id }));
  }, []);
  const mhProviderPanelFeatureToggle = window.FEATURE_TOGGLES.provider_panel_mental_health_enabled;
  const isVisibleProviderPanelTab = specialty !== specialtyCodes.MENTAL_HEALTH || mhProviderPanelFeatureToggle;
  const shouldRedirectToPatientPanel = !isVisibleProviderPanelTab && location.pathname === providerPanelPath

  useEffect(() => {
    setCurrentTab(location.pathname === patientPanelPath ? PATIENT_PANEL_PATH : PROVIDER_PANEL_PATH);
  }, [location]);

  const handleTabChange = useCallback((tab) => {
    history.push(tab === PATIENT_PANEL_PATH ? patientPanelPath : providerPanelPath);
  }, [history, patientPanelPath, providerPanelPath]);

  if (!window.FEATURE_TOGGLES.provider_panel_view) {
    return (
      <PatientPanel
        {...patientPanel}
      />
    );
  }

  const isSpecialtyInitialized = !!specialty;

  return (
    <React.Fragment>
      <SpecialtyFilter
        specialties={specialties}
      />

      <Tabs
        value={currentTab}
        onChange={handleTabChange}
      >
        <Tab
          value={PATIENT_PANEL_PATH}
          disabled={!isSpecialtyInitialized}
        >
          {translate(null, 'patient_panel', 'tab')}
        </Tab>

        {isVisibleProviderPanelTab &&
          (<Tab
            value={PROVIDER_PANEL_PATH}
            disabled={!isSpecialtyInitialized}
          >
            {translate(null, 'provider_panel', 'tab')}
          </Tab>)}
      </Tabs>
      {
        isSpecialtyInitialized ? (
          <Switch>
            <Route
              exact
              path={patientPanelPath}
              component={() => (
                <PatientPanel
                  {...patientPanel}
                  specialty={specialty}
                  specialties={specialties}
                  enableStoredPreferences={enableStoredPreferences}
                />
              )}
            />
            <Route
              exact
              path={providerPanelPath}
            >
              {shouldRedirectToPatientPanel ? (
                <Redirect to={patientPanelPath} />
               ) : (
                <ProviderPanel
                  {...providerPanel}
                  specialty={specialty}
                  enableStoredPreferences={enableStoredPreferences}
                />
              )}
            </Route>
          </Switch>
        ) : (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        )
      }
    </React.Fragment>
  );
};

const PanelsContainer = ({ specialties, patientPanel, providerPanel }) => (
  <Router>
    <SpecialtyProvider specialties={specialties}>
      <Panels
        specialties={specialties}
        patientPanel={patientPanel}
        providerPanel={providerPanel}
      />
    </SpecialtyProvider>
  </Router>
);

export default PanelsContainer;
